// src/utils/entityConstants.js
// Constantes pour les entités (sociétés) - Version expert-comptable

/**
 * Formes juridiques avec spécificités du dirigeant
 *
 * Note sur l'option IR (Article 239 bis AB CGI):
 * - Les SAS, SARL, SA peuvent opter pour l'IR (max 5 exercices)
 * - Conditions: < 5 ans d'existence, < 50 salariés, CA < 10M€
 * - Option irrévocable et non renouvelable
 * - SCI/SNC: IR par défaut, option IS possible (irrévocable)
 */
export const LEGAL_FORMS = [
  {
    value: "SAS",
    label: "SAS",
    fullName: "Société par Actions Simplifiée",
    category: "capital",
    managerType: "ASSIMILE_SALARIE",
    managerTitle: "Président",
    defaultTaxRegime: "IS",
    canOptIR: true, // Art. 239 bis AB CGI - Option IR possible (5 ans max)
    requiresLegalReserve: true, // Sociétés commerciales: réserve légale obligatoire
    description: "Président assimilé salarié, grande flexibilité",
    note: "SASU si 1 associé, SAS si 2+"
  },
  {
    value: "SARL",
    label: "SARL",
    fullName: "Société à Responsabilité Limitée",
    category: "personnes",
    managerType: "TNS_OU_ASSIMILE", // Selon majoritaire ou non
    managerTitle: "Gérant",
    defaultTaxRegime: "IS",
    canOptIR: true, // Art. 239 bis AB CGI - Option IR possible (5 ans max)
    requiresLegalReserve: true, // Sociétés commerciales: réserve légale obligatoire
    description: "Gérant TNS si majoritaire, sinon AS",
    note: "EURL si 1 associé, SARL si 2+"
  },
  {
    value: "SA",
    label: "SA",
    fullName: "Société Anonyme",
    category: "capital",
    managerType: "ASSIMILE_SALARIE",
    managerTitle: "Président / DG",
    defaultTaxRegime: "IS",
    canOptIR: true, // Art. 239 bis AB CGI - Option IR possible (5 ans max)
    requiresLegalReserve: true, // Sociétés commerciales: réserve légale obligatoire
    description: "7+ actionnaires, capital min 37k€"
  },
  {
    value: "SCI",
    label: "SCI",
    fullName: "Société Civile Immobilière",
    category: "civile",
    managerType: "AUCUN", // Pas de charges si pas de rémunération
    managerTitle: "Gérant",
    defaultTaxRegime: "IR",
    canOptIR: true, // IR par défaut, option IS possible mais IRREVERSIBLE
    canOptIS: true, // Option IS irrévocable
    isOptionIrreversible: true, // Warning: passage IS = définitif
    requiresLegalReserve: false, // Sociétés civiles: pas de réserve légale obligatoire
    description: "Gestion immobilière, IR par défaut"
  },
  {
    value: "SNC",
    label: "SNC",
    fullName: "Société en Nom Collectif",
    category: "personnes",
    managerType: "TNS",
    managerTitle: "Gérant",
    defaultTaxRegime: "IR",
    canOptIR: true, // IR par défaut, option IS possible mais IRREVERSIBLE
    canOptIS: true, // Option IS irrévocable
    isOptionIrreversible: true, // Warning: passage IS = définitif
    requiresLegalReserve: false, // Sociétés civiles: pas de réserve légale obligatoire
    description: "Associés responsables indéfiniment"
  },
  {
    value: "SELARL",
    label: "SELARL",
    fullName: "Société d'Exercice Libéral",
    category: "liberal",
    managerType: "TNS",
    managerTitle: "Gérant",
    defaultTaxRegime: "IS",
    canOptIR: true, // Art. 239 bis AB CGI applicable aux SELARL
    requiresLegalReserve: true, // SELARL = SARL libérale, réserve légale obligatoire
    description: "Professions libérales réglementées"
  },
  {
    value: "HOLDING",
    label: "Holding",
    fullName: "Société Holding",
    category: "capital",
    managerType: "ASSIMILE_SALARIE",
    managerTitle: "Président",
    defaultTaxRegime: "IS",
    canOptIR: false, // Holdings généralement exclues du régime IR (activité patrimoniale)
    requiresLegalReserve: true, // Holding = généralement SAS/SARL, réserve légale obligatoire
    description: "Détention et gestion de participations"
  },
];

/**
 * Types de dirigeant avec charges sociales
 */
export const MANAGER_TYPES = {
  ASSIMILE_SALARIE: {
    label: "Assimilé salarié",
    description: "Régime général de la Sécurité Sociale",
    socialChargesRate: 0.82, // ~82% de charges sur le net (≈45% sur le brut)
    chargesOnBrut: 0.45, // 45% de charges patronales + salariales
    minimumBase: 0, // Pas de cotisation minimum
    hasUnemployment: false, // Pas d'assurance chômage sauf cas particuliers
    retirement: "CNAV",
  },
  TNS: {
    label: "TNS (Travailleur Non Salarié)",
    description: "Sécurité Sociale des Indépendants (SSI)",
    socialChargesRate: 0.45, // ~45% de charges sur le net
    chargesOnBrut: 0.31, // ~31% sur le brut équivalent
    minimumBase: 12096, // Cotisation minimale sur cette base (2024)
    hasUnemployment: false,
    retirement: "SSI",
  },
  AUCUN: {
    label: "Aucune rémunération",
    description: "Pas de charges sociales",
    socialChargesRate: 0,
    chargesOnBrut: 0,
    minimumBase: 0,
    hasUnemployment: false,
    retirement: null,
  },
};

/**
 * Taux de charges sociales détaillés par année
 */
export const SOCIAL_CHARGES_RATES = {
  2024: {
    TNS: {
      maladie: 0.065,           // 6.5% (jusqu'à 5 PASS)
      maladieReduced: 0.04,     // 4% (revenus < 40% PASS)
      retraiteBase: 0.1775,     // 17.75%
      retraiteComplementaire: 0.07, // 7%
      invaliditeDecesBase: 0.013,
      allocFamiliales: 0.031,   // 3.1% (taux plein)
      allocFamilialesReduced: 0, // 0% si revenus < 110% PASS
      csg: 0.097,               // 9.7%
      crds: 0.005,              // 0.5%
      formationPro: 0.0025,     // 0.25%
      total: 0.45,              // Estimation globale simplifiée
    },
    ASSIMILE_SALARIE: {
      chargesPatronales: 0.42,  // ~42% du brut
      chargesSalariales: 0.22,  // ~22% du brut
      total: 0.45,              // Total sur brut (net = brut * 0.78, coût = brut * 1.42)
    }
  },
  2023: {
    TNS: { total: 0.45 },
    ASSIMILE_SALARIE: { total: 0.45 },
  },
};

/**
 * Régimes fiscaux avec options
 */
export const TAX_REGIMES = [
  {
    value: "IS",
    label: "IS",
    fullName: "Impôt sur les Sociétés",
    description: "La société paie l'impôt",
    icon: "🏢"
  },
  {
    value: "IR",
    label: "IR",
    fullName: "Impôt sur le Revenu",
    description: "Imposé chez les associés",
    icon: "👤"
  },
];

/**
 * Types d'activité - en chips
 */
export const ACTIVITY_TYPES = [
  {
    value: "BIC",
    label: "BIC",
    fullName: "Bénéfices Industriels et Commerciaux",
    description: "Commerce, industrie, artisanat",
    icon: "🏭"
  },
  {
    value: "BNC",
    label: "BNC",
    fullName: "Bénéfices Non Commerciaux",
    description: "Professions libérales, services",
    icon: "💼"
  },
  {
    value: "BA",
    label: "BA",
    fullName: "Bénéfices Agricoles",
    description: "Activités agricoles",
    icon: "🌾"
  },
  {
    value: "LMNP",
    label: "LMNP",
    fullName: "Loueur Meublé Non Pro",
    description: "Location meublée",
    icon: "🏠"
  },
  {
    value: "LMP",
    label: "LMP",
    fullName: "Loueur Meublé Pro",
    description: "Location meublée professionnelle",
    icon: "🏨"
  },
];

/**
 * Régime mère-fille (export de compatibilité)
 */
export const MOTHER_DAUGHTER_REGIME = {
  minimumHolding: 5, // 5% minimum
  holdingPeriod: 24, // mois
  exemptionRate: 95, // 95% exonéré
  taxableQuoteShare: 5, // 5% réintégré (quote-part de frais)
};

/**
 * Options avancées de structuration
 */
export const ADVANCED_OPTIONS = {
  INTEGRATION_FISCALE: {
    label: "Intégration fiscale",
    description: "Groupe fiscal (mère détient ≥95% des filiales)",
    minHolding: 95,
    benefit: "Compensation des résultats déficitaires/bénéficiaires",
  },
  REGIME_MERE_FILLE: {
    label: "Régime mère-fille",
    description: "Exonération quasi-totale des dividendes remontés",
    minHolding: 5, // 5% minimum
    minHoldingPeriod: 24, // mois
    exemptionRate: 95, // 95% exonéré (5% réintégré)
  },
  JEI: {
    label: "JEI",
    fullName: "Jeune Entreprise Innovante",
    description: "Exonérations sociales et fiscales R&D",
    benefits: ["Exo IS 100% an 1", "Exo IS 50% an 2", "Exo charges sociales R&D"],
  },
  CIR: {
    label: "CIR",
    fullName: "Crédit Impôt Recherche",
    description: "30% des dépenses R&D éligibles",
    rate: 0.30,
    ceiling: null, // Pas de plafond
  },
  CII: {
    label: "CII",
    fullName: "Crédit Impôt Innovation",
    description: "20% des dépenses innovation (PME)",
    rate: 0.20,
    ceiling: 400000, // Plafond 400k€ de dépenses
  },
  ZONING: {
    label: "Zones exonérées",
    description: "ZFU, ZRR, BER, QPV",
    options: ["ZFU", "ZRR", "BER", "QPV"],
  },
};

/**
 * Lignes de charges courantes pré-remplies
 */
export const COMMON_CHARGE_LINES = [
  {
    key: "loyer",
    label: "Loyer & charges locatives",
    icon: "🏠",
    type: "fixed", // fixed ou percent
    defaultValue: 12000, // €/an
    category: "immobilier"
  },
  {
    key: "internet_tel",
    label: "Internet & téléphonie",
    icon: "📱",
    type: "fixed",
    defaultValue: 1200,
    category: "services"
  },
  {
    key: "transport",
    label: "Transport & déplacements",
    icon: "🚗",
    type: "fixed",
    defaultValue: 3000,
    category: "deplacement"
  },
  {
    key: "fournitures",
    label: "Fournitures & consommables",
    icon: "📦",
    type: "fixed",
    defaultValue: 1500, // €/an
    category: "exploitation"
  },
  {
    key: "assurances",
    label: "Assurances (RC Pro, etc.)",
    icon: "🛡️",
    type: "fixed",
    defaultValue: 2000,
    category: "assurance"
  },
  {
    key: "comptable",
    label: "Expert-comptable",
    icon: "📊",
    type: "fixed",
    defaultValue: 3000,
    category: "services"
  },
  {
    key: "juridique",
    label: "Frais juridiques",
    icon: "⚖️",
    type: "fixed",
    defaultValue: 1000,
    category: "services"
  },
  {
    key: "formation",
    label: "Formation continue",
    icon: "📚",
    type: "fixed",
    defaultValue: 1500,
    category: "formation"
  },
  {
    key: "marketing",
    label: "Marketing & publicité",
    icon: "📣",
    type: "fixed",
    defaultValue: 2000, // €/an
    category: "commercial"
  },
  {
    key: "sous_traitance",
    label: "Sous-traitance",
    icon: "🤝",
    type: "fixed",
    defaultValue: 0, // €/an
    category: "exploitation"
  },
  {
    key: "achats_marchandises",
    label: "Achats de marchandises",
    icon: "🛒",
    type: "fixed",
    defaultValue: 0, // €/an
    category: "exploitation"
  },
  {
    key: "salaires_employes",
    label: "Salaires employés (hors dirigeant)",
    icon: "👥",
    type: "fixed",
    defaultValue: 0,
    category: "personnel"
  },
  {
    key: "charges_sociales_employes",
    label: "Charges sociales employés",
    icon: "📋",
    type: "percent", // % des salaires employés
    defaultValue: 45,
    linkedTo: "salaires_employes",
    category: "personnel"
  },
  {
    key: "dotation_amortissements",
    label: "Dotation aux amortissements",
    icon: "📉",
    type: "fixed",
    defaultValue: 0,
    category: "dotations"
  },
  {
    key: "autres_charges",
    label: "Autres charges",
    icon: "📝",
    type: "fixed",
    defaultValue: 0,
    category: "autres"
  },
];

/**
 * Catégories de charges pour regroupement
 */
export const CHARGE_CATEGORIES = [
  { key: "personnel", label: "Personnel", icon: "👥" },
  { key: "immobilier", label: "Immobilier", icon: "🏠" },
  { key: "services", label: "Services externes", icon: "🔧" },
  { key: "exploitation", label: "Exploitation", icon: "⚙️" },
  { key: "commercial", label: "Commercial", icon: "📣" },
  { key: "deplacement", label: "Déplacements", icon: "🚗" },
  { key: "assurance", label: "Assurances", icon: "🛡️" },
  { key: "formation", label: "Formation", icon: "📚" },
  { key: "dotations", label: "Dotations", icon: "📉" },
  { key: "autres", label: "Autres", icon: "📝" },
];

/**
 * Hypothèses par défaut simplifiées
 */
export const DEFAULT_HYPOTHESES = {
  personnelPercent: 0,         // Géré séparément
  achatsPercent: 15,           // 15% du CA
  loyersFixed: 12000,          // 12k€/an
  amortPercent: 3,             // 3% du CA
  dividendPercent: 60,         // 60% du résultat net
  // Rémunération dirigeant par défaut
  managerRemunerationMonthly: 3000, // 3000€ net/mois
};

/**
 * Taux IS par année
 */
export const IS_RATES_BY_YEAR = {
  2025: { reducedRateLimit: 42500, reducedRate: 0.15, normalRate: 0.25 },
  2024: { reducedRateLimit: 42500, reducedRate: 0.15, normalRate: 0.25 },
  2023: { reducedRateLimit: 42500, reducedRate: 0.15, normalRate: 0.25 },
  2022: { reducedRateLimit: 38120, reducedRate: 0.15, normalRate: 0.25 },
  2021: { reducedRateLimit: 38120, reducedRate: 0.15, normalRate: 0.2667 },
  2020: { reducedRateLimit: 38120, reducedRate: 0.15, normalRate: 0.28 },
};

/**
 * Plafond Sécurité Sociale par année
 */
export const PASS_BY_YEAR = {
  2025: 47100, // Estimation
  2024: 46368,
  2023: 43992,
  2022: 41136,
  2021: 41136,
};

/**
 * Types de sources de CA
 */
export const SOURCE_TYPES = [
  {
    value: "CA_PRINCIPAL",
    label: "CA Principal",
    icon: "📊",
    color: "#3B82F6",
    description: "Activité principale"
  },
  {
    value: "CA_SECONDAIRE",
    label: "CA Secondaire",
    icon: "📈",
    color: "#10B981",
    description: "Activité annexe"
  },
  {
    value: "DIVIDENDES_RECUS",
    label: "Dividendes reçus",
    icon: "💰",
    color: "#F59E0B",
    description: "Dividendes de filiales"
  },
  {
    value: "SUBVENTIONS",
    label: "Subventions",
    icon: "🎁",
    color: "#8B5CF6",
    description: "Aides et subventions"
  },
];

/**
 * Codes APE les plus courants (échantillon - fichier complet séparé)
 */
export const COMMON_APE_CODES = [
  { code: "6201Z", label: "Programmation informatique" },
  { code: "6202A", label: "Conseil en systèmes informatiques" },
  { code: "6311Z", label: "Traitement de données, hébergement" },
  { code: "7022Z", label: "Conseil pour les affaires" },
  { code: "7021Z", label: "Conseil en relations publiques" },
  { code: "7010Z", label: "Activités des sièges sociaux" },
  { code: "7112B", label: "Ingénierie, études techniques" },
  { code: "7490B", label: "Activités spécialisées de design" },
  { code: "7311Z", label: "Activités des agences de publicité" },
  { code: "4791B", label: "Vente à distance sur catalogue" },
  { code: "4711F", label: "Hypermarchés" },
  { code: "5610A", label: "Restauration traditionnelle" },
  { code: "6820A", label: "Location de logements" },
  { code: "6820B", label: "Location de terrains et autres biens immobiliers" },
  { code: "6831Z", label: "Agences immobilières" },
  { code: "8559A", label: "Formation continue d'adultes" },
  { code: "9001Z", label: "Arts du spectacle vivant" },
  { code: "9003A", label: "Création artistique" },
  { code: "6910Z", label: "Activités juridiques" },
  { code: "6920Z", label: "Activités comptables" },
];

// ==================== HELPERS ====================

/**
 * Obtenir les infos complètes d'une forme juridique
 */
export const getLegalFormInfo = (value) => {
  return LEGAL_FORMS.find(f => f.value === value) || LEGAL_FORMS[0];
};

/**
 * Obtenir le type de dirigeant pour une forme juridique
 */
export const getManagerTypeForLegalForm = (legalForm, isGérantMajoritaire = true) => {
  const form = getLegalFormInfo(legalForm);
  if (form.managerType === "TNS_OU_ASSIMILE") {
    return isGérantMajoritaire ? "TNS" : "ASSIMILE_SALARIE";
  }
  return form.managerType;
};

/**
 * Calculer les charges sociales du dirigeant
 */
export const calculateManagerSocialCharges = (netRemuneration, managerType, year = 2024) => {
  const typeInfo = MANAGER_TYPES[managerType];
  if (!typeInfo || managerType === "AUCUN") {
    return { charges: 0, brut: 0, cout: 0 };
  }

  // eslint-disable-next-line no-unused-vars
  const rates = SOCIAL_CHARGES_RATES[year] || SOCIAL_CHARGES_RATES[2024];

  if (managerType === "TNS") {
    // TNS: charges sur le net
    const charges = netRemuneration * typeInfo.socialChargesRate;
    return {
      net: netRemuneration,
      charges: Math.round(charges),
      brut: Math.round(netRemuneration + charges),
      cout: Math.round(netRemuneration + charges),
    };
  } else {
    // Assimilé salarié: brut = net / 0.78, coût = brut * 1.42
    const brut = netRemuneration / 0.78;
    const chargesPatronales = brut * 0.42;
    const cout = brut + chargesPatronales;
    return {
      net: netRemuneration,
      brut: Math.round(brut),
      chargesSalariales: Math.round(brut * 0.22),
      chargesPatronales: Math.round(chargesPatronales),
      charges: Math.round(brut - netRemuneration + chargesPatronales),
      cout: Math.round(cout),
    };
  }
};

/**
 * Calculer les charges patronales depuis le salaire BRUT
 * Input: salaire BRUT mensuel ou annuel
 * Output: charges patronales, coût total entreprise
 */
export const calculateChargesFromBrut = (brutRemuneration, personType, year = 2024) => {
  if (!brutRemuneration || personType === 'AUCUN') {
    return { brut: 0, chargesPatronales: 0, cout: 0, tauxCharges: 0, label: '', statusDisplay: '' };
  }

  let tauxCharges;
  let label;
  let statusDisplay;

  // TNS (gérant majoritaire SARL, EURL): cotisations SSI ~45% de la rémunération nette
  // La rémunération TNS est le "net avant IR", les charges sont calculées dessus
  if (personType === 'TNS') {
    tauxCharges = 0.45;
    label = 'TNS (~45%)';
    statusDisplay = 'TNS';
  }
  // Assimilé salarié (président SAS/SASU, gérant minoritaire/égalitaire SARL)
  // Charges patronales sur le brut: ~50-55% (régime général complet)
  else if (personType === 'AS' || personType === 'ASSIMILE_SALARIE') {
    tauxCharges = 0.54; // Charges patronales régime général: ~50-55%
    label = 'AS (~54%)';
    statusDisplay = 'AS';
  }
  // Employé standard: charges patronales ~42-45% du brut
  else {
    tauxCharges = 0.45;
    label = 'Salarié (~45%)';
    statusDisplay = 'Salarié';
  }

  const chargesPatronales = Math.round(brutRemuneration * tauxCharges);
  const cout = Math.round(brutRemuneration + chargesPatronales);

  // Charges salariales et NET (ce que le salarié reçoit réellement)
  // ASSIMILE_SALARIE: ~22% de charges salariales => NET = BRUT * 0.78
  // TNS: pas de charges salariales (cotisations déjà calculées)
  let tauxChargesSalariales = 0;
  if (personType === 'AS' || personType === 'ASSIMILE_SALARIE') {
    tauxChargesSalariales = 0.22; // ~22% du brut
  } else if (personType !== 'TNS') {
    tauxChargesSalariales = 0.22; // Salarié standard aussi ~22%
  }
  const chargesSalariales = Math.round(brutRemuneration * tauxChargesSalariales);
  const net = brutRemuneration - chargesSalariales;

  return {
    brut: brutRemuneration,
    chargesPatronales,
    chargesSalariales,
    net,
    cout,
    tauxCharges,
    tauxChargesSalariales,
    label,
    statusDisplay
  };
};

/**
 * Calculate charges from NET salary (what the person receives)
 * Input: netRemuneration (what the manager receives monthly/annually × months)
 * Output: brut, charges patronales, coût total entreprise
 */
export const calculateChargesFromNet = (netRemuneration, personType, year = 2024) => {
  if (!netRemuneration || personType === 'AUCUN') {
    return { brut: 0, net: 0, chargesPatronales: 0, chargesSalariales: 0, cout: 0, tauxCharges: 0, label: '', statusDisplay: '' };
  }

  let tauxChargesPatronales;
  let tauxChargesSalariales = 0;
  let label;
  let statusDisplay;

  // TNS (gérant majoritaire SARL, EURL): cotisations SSI ~45% de la rémunération nette
  // Pour TNS, le NET = la rémunération, les charges sont calculées dessus
  if (personType === 'TNS') {
    tauxChargesPatronales = 0.45;
    tauxChargesSalariales = 0; // Pas de charges salariales pour TNS
    label = 'TNS (~45%)';
    statusDisplay = 'TNS';

    const brut = netRemuneration; // Pour TNS, brut = net (pas de charges salariales)
    const chargesPatronales = Math.round(netRemuneration * tauxChargesPatronales);
    const cout = Math.round(netRemuneration + chargesPatronales);

    return {
      brut,
      net: netRemuneration,
      chargesPatronales,
      chargesSalariales: 0,
      cout,
      tauxCharges: tauxChargesPatronales,
      tauxChargesSalariales: 0,
      label,
      statusDisplay
    };
  }

  // Assimilé salarié (président SAS/SASU, gérant minoritaire/égalitaire SARL)
  // NET = BRUT × 0.78 => BRUT = NET / 0.78
  // Charges patronales sur le brut: ~54%
  if (personType === 'AS' || personType === 'ASSIMILE_SALARIE') {
    tauxChargesPatronales = 0.54;
    tauxChargesSalariales = 0.22;
    label = 'AS (~54%)';
    statusDisplay = 'AS';
  }
  // Employé standard: charges patronales ~45% du brut
  else {
    tauxChargesPatronales = 0.45;
    tauxChargesSalariales = 0.22;
    label = 'Salarié (~45%)';
    statusDisplay = 'Salarié';
  }

  // Calculate BRUT from NET: brut = net / (1 - tauxChargesSalariales)
  const brut = Math.round(netRemuneration / (1 - tauxChargesSalariales));
  const chargesSalariales = Math.round(brut * tauxChargesSalariales);
  const chargesPatronales = Math.round(brut * tauxChargesPatronales);
  const cout = Math.round(brut + chargesPatronales);

  return {
    brut,
    net: netRemuneration,
    chargesPatronales,
    chargesSalariales,
    cout,
    tauxCharges: tauxChargesPatronales,
    tauxChargesSalariales,
    label,
    statusDisplay
  };
};

/**
 * Obtenir les taux IS pour une année
 */
export const getISRatesForYear = (year) => {
  return IS_RATES_BY_YEAR[year] || IS_RATES_BY_YEAR[2024];
};

/**
 * Obtenir les infos d'un type de source
 */
export const getSourceTypeInfo = (value) => {
  return SOURCE_TYPES.find(s => s.value === value) || SOURCE_TYPES[0];
};

/**
 * Obtenir le PASS pour une année
 */
export const getPASSForYear = (year) => {
  return PASS_BY_YEAR[year] || PASS_BY_YEAR[2024];
};

/**
 * Vérifier si une société peut bénéficier du régime mère-fille
 */
export const canApplyMotherDaughterRegime = (holdingPercentage, holdingMonths) => {
  const regime = ADVANCED_OPTIONS.REGIME_MERE_FILLE;
  return holdingPercentage >= regime.minHolding && holdingMonths >= regime.minHoldingPeriod;
};

/**
 * Calculer l'exonération mère-fille
 */
export const calculateMotherDaughterExemption = (dividendsReceived) => {
  const regime = ADVANCED_OPTIONS.REGIME_MERE_FILLE;
  const exempt = dividendsReceived * (regime.exemptionRate / 100);
  const taxable = dividendsReceived - exempt; // 5% réintégré
  return { exempt, taxable, total: dividendsReceived };
};

// ==================== PLAN COMPTABLE GÉNÉRAL (PCG) ====================

/**
 * Catégories de PRODUITS selon le PCG (Classe 7)
 */
export const PCG_PRODUITS = [
  {
    compte: '70',
    label: "Chiffre d'affaires",
    description: "Ventes de produits, prestations de services",
    icon: '💼',
    color: '#3B82F6',
    allowMultiple: true, // Peut avoir plusieurs lignes (CA principal, secondaires)
    hasChargesPropres: true, // Peut avoir des charges variables liées
  },
  {
    compte: '74',
    label: "Subventions d'exploitation",
    description: "Aides, subventions (BPI, régions, etc.)",
    icon: '🎁',
    color: '#8B5CF6',
    allowMultiple: true,
    hasChargesPropres: false,
  },
  {
    compte: '75',
    label: "Autres produits de gestion",
    description: "Redevances, revenus immobiliers, etc.",
    icon: '📋',
    color: '#10B981',
    allowMultiple: true,
    hasChargesPropres: false,
  },
  {
    compte: '76',
    label: "Produits financiers",
    description: "Intérêts, dividendes reçus",
    icon: '💰',
    color: '#F59E0B',
    allowMultiple: false,
    hasChargesPropres: false,
  },
  {
    compte: '77',
    label: "Produits exceptionnels",
    description: "Plus-values, reprises provisions",
    icon: '⭐',
    color: '#EC4899',
    allowMultiple: false,
    hasChargesPropres: false,
  },
];

/**
 * Catégories de CHARGES selon le PCG (Classe 6)
 */
export const PCG_CHARGES = [
  {
    compte: '60',
    label: "Achats",
    description: "Matières, marchandises, sous-traitance",
    icon: '🛒',
    color: '#EF4444',
    isVariable: true, // Généralement proportionnel au CA
    detailLines: [
      { key: 'matieres', label: 'Matières premières', compte: '601' },
      { key: 'marchandises', label: 'Marchandises', compte: '607' },
      { key: 'soustraitance', label: 'Sous-traitance', compte: '604' },
      { key: 'fournitures', label: 'Fournitures', compte: '606' },
    ],
  },
  {
    compte: '61-62',
    label: "Services extérieurs",
    description: "Loyer, assurance, comptable, internet...",
    icon: '🏢',
    color: '#F59E0B',
    isVariable: false,
    detailLines: [
      { key: 'loyer', label: 'Loyer & charges locatives', compte: '613' },
      { key: 'entretien', label: 'Entretien & réparations', compte: '615' },
      { key: 'assurances', label: 'Assurances', compte: '616' },
      { key: 'honoraires', label: 'Honoraires (comptable, avocat)', compte: '622' },
      { key: 'publicite', label: 'Publicité & communication', compte: '623' },
      { key: 'deplacements', label: 'Déplacements & missions', compte: '625' },
      { key: 'telecom', label: 'Télécom & internet', compte: '626' },
      { key: 'bancaires', label: 'Frais bancaires', compte: '627' },
    ],
  },
  {
    compte: '63',
    label: "Impôts, taxes et versements",
    description: "CFE, CVAE, taxes sur salaires...",
    icon: '🏛️',
    color: '#8B5CF6',
    isVariable: false,
    detailLines: [
      { key: 'cfe', label: 'CFE', compte: '63511', help: 'Cotisation Foncière des Entreprises - exonérée 1ère année, -50% 2e année' },
      { key: 'cvae', label: 'CVAE', compte: '63511', help: 'Cotisation sur la Valeur Ajoutée - si CA > 500k€' },
      { key: 'taxeApprentissage', label: 'Taxe apprentissage', compte: '6312', help: '0.68% de la masse salariale brute' },
      { key: 'formationPro', label: 'Contribution formation', compte: '6333', help: '0.55% (<11 sal.) ou 1% (≥11 sal.)' },
      { key: 'effortConstruction', label: 'Effort construction (PEEC)', compte: '6334', help: '0.45% si ≥50 salariés' },
      { key: 'agefiph', label: 'AGEFIPH', compte: '6335', help: 'Si ≥20 salariés sans quota handicap' },
      { key: 'versementMobilite', label: 'Versement mobilité', compte: '6336', help: 'Variable selon zone, si ≥11 salariés' },
    ],
  },
  {
    compte: '64',
    label: "Charges de personnel",
    description: "Rémunérations et charges sociales",
    icon: '👥',
    color: '#3B82F6',
    isVariable: false,
    detailLines: [
      { key: 'remunerationDirigeants', label: 'Rémunération dirigeant(s)', compte: '641' },
      { key: 'chargesSocialesDirigeants', label: 'Charges sociales dirigeant(s)', compte: '645' },
      { key: 'salairesEmployes', label: 'Salaires employés', compte: '641' },
      { key: 'chargesSocialesEmployes', label: 'Charges sociales employés', compte: '645' },
    ],
  },
  {
    compte: '65',
    label: "Autres charges de gestion",
    description: "Redevances, pertes sur créances...",
    icon: '📝',
    color: '#6B7280',
    isVariable: false,
    detailLines: [
      { key: 'redevances', label: 'Redevances', compte: '651' },
      { key: 'pertesCreances', label: 'Pertes sur créances', compte: '654' },
    ],
  },
  {
    compte: '66',
    label: "Charges financières",
    description: "Intérêts d'emprunts, agios...",
    icon: '🏦',
    color: '#DC2626',
    isVariable: false,
    detailLines: [
      { key: 'interetsEmprunts', label: "Intérêts d'emprunts", compte: '6611' },
      { key: 'agios', label: 'Agios & frais bancaires', compte: '6616' },
    ],
  },
  {
    compte: '68',
    label: "Dotations amort. & provisions",
    description: "Amortissements, provisions pour risques",
    icon: '📉',
    color: '#9CA3AF',
    isVariable: false,
    detailLines: [
      { key: 'dotationsAmortissements', label: 'Dotations aux amortissements', compte: '681' },
      { key: 'dotationsProvisions', label: 'Dotations aux provisions', compte: '686' },
    ],
  },
];

/**
 * Types de produits pour l'ajout rapide
 */
export const PRODUIT_TYPES = [
  { value: 'ca_principal', label: 'CA Principal', compte: '70', icon: '💼' },
  { value: 'ca_secondaire', label: 'CA Secondaire', compte: '70', icon: '📊' },
  { value: 'subvention', label: 'Subvention', compte: '74', icon: '🎁' },
  { value: 'autres_produits', label: 'Autres produits', compte: '75', icon: '📋' },
  { value: 'produits_financiers', label: 'Produits financiers', compte: '76', icon: '💰' },
];

/**
 * Obtenir une catégorie de produit PCG par son compte
 */
export const getPCGProduit = (compte) => {
  return PCG_PRODUITS.find(p => p.compte === compte);
};

/**
 * Obtenir une catégorie de charge PCG par son compte
 */
export const getPCGCharge = (compte) => {
  return PCG_CHARGES.find(c => c.compte === compte);
};

// ==================== MANAGER & EMPLOYEE CONSTANTS ====================

/**
 * Rôles de dirigeant
 */
export const MANAGER_ROLES = [
  { value: 'president', label: 'Président' },
  { value: 'dg', label: 'Directeur Général' },
  { value: 'gerant', label: 'Gérant' },
  { value: 'gerant_majoritaire', label: 'Gérant majoritaire' },
  { value: 'gerant_minoritaire', label: 'Gérant minoritaire' },
  { value: 'associe_unique', label: 'Associé unique' },
];

/**
 * Statuts sociaux des dirigeants
 */
export const MANAGER_STATUSES = [
  { value: 'TNS', label: 'TNS (Travailleur Non Salarié)', chargeRate: 0.45 },
  { value: 'ASSIMILE_SALARIE', label: 'Assimilé salarié', chargeRate: 0.54 },
  { value: 'AUCUN', label: 'Pas de rémunération', chargeRate: 0 },
];

/**
 * Régimes TVA
 */
export const TVA_REGIMES = [
  { value: 'non_assujetti', label: 'Non assujetti', description: 'Franchise en base de TVA' },
  { value: 'reel_simplifie', label: 'Réel simplifié', description: 'CA < 840k€ (services) ou 789k€ (biens)' },
  { value: 'reel_normal', label: 'Réel normal', description: 'Déclaration mensuelle' },
  { value: 'mini_reel', label: 'Mini-réel', description: 'Déclaration mensuelle avec seuils simplifiés' },
];

/**
 * Taux de charges par type
 */
export const CHARGE_RATES = {
  TNS: 0.45,                    // 45% sur net pour TNS
  ASSIMILE_SALARIE: 0.54,       // 54% charges patronales sur brut
  EMPLOYEE: 0.45,               // 45% charges patronales sur brut employé
  ACRE_REDUCTION: 0.50,         // 50% réduction ACRE année 1
};
